import type { ComponentProps } from 'react';

import { Button } from '@change/ds-core-ui/Button';

import { useResolvedLabel } from '#src/app/shared/hooks/i18n';
import type { ShareTrackingDataOverride } from '#src/app/shared/hooks/share';

import { ICON_BY_CHANNEL } from './constants';
import { useShareChannelButton } from './hooks/useShareChannelButton';
import type { ShareChannelButtonConfig } from './types';

export type { ShareChannelButtonConfig } from './types';

type Props = {
	buttonConfig: ShareChannelButtonConfig;
	onBeforeShare?: () => void;
	/**
	 * Runs once the share resolves. `success` is false when the action failed or the viewer cancelled
	 * it, so anything that reports the share as done belongs here rather than in `onBeforeShare`.
	 */
	onAfterShare?: (success: boolean) => void;
	variant?: ComponentProps<typeof Button>['variant'];
	trackingData?: ShareTrackingDataOverride;
};

export const ShareChannelButton = ({
	buttonConfig: { ShareAction, label, channel, successLabel },
	onBeforeShare,
	onAfterShare,
	variant = 'primary',
	trackingData,
	...rest // for data attributes
}: Props): React.JSX.Element => {
	const IconComponent = ICON_BY_CHANNEL[channel];

	const { data, actions } = useShareChannelButton({ label, successLabel });

	const buttonLabel = useResolvedLabel(data.buttonLabel);

	return (
		<ShareAction
			onBeforeAction={onBeforeShare}
			onAfterAction={(success) => {
				actions.afterShare(success);
				onAfterShare?.(success);
			}}
			trackingData={trackingData}
		>
			<Button
				aria-label={buttonLabel}
				aria-disabled={data.clickedState}
				data-testid={`${channel}-share-button`}
				variant={variant}
				size="small"
				leadingIcon={IconComponent}
				{...rest}
			>
				{buttonLabel}
			</Button>
		</ShareAction>
	);
};
